import type { IntlShape } from 'react-intl';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { VIEW_METHOD } from '@atlaskit/editor-common/analytics/types/inline-comment-events';
import { getNodeCopiedAnalyticsPayload } from '@atlaskit/editor-common/clipboard';
import { copyDomNode, toDOM } from '@atlaskit/editor-common/copy-button';
import { NATIVE_EMBED_EXTENSION_KEY } from '@atlaskit/editor-common/extensions/manifest-helpers';
import type { Command } from '@atlaskit/editor-common/types/command';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { findDomRefAtPos, type ContentNodeWithPos } from '@atlaskit/editor-prosemirror/utils';
import { EDITOR_TOOLBAR_HANDLER_KEYS } from '@atlaskit/native-embeds-common/constants';
import type {
	AlignmentValue,
	EditorToolbarHandlerContext,
	EditorToolbarHandlers,
	NativeEmbedAppearance,
	NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type {
	EditorPluginNativeEmbedsPlugin,
	EditorToolbarHandlersWithDefaults,
} from '../../nativeEmbedsPluginType';
import {
	createCopyLinkCommand,
	createDeleteCommand,
	createOpenInNewWindowCommand,
	createToggleAlwaysShowTitle,
	createUpdateAlignmentCommand,
} from '../commands';
import { setNativeEmbedAppearance } from '../commands/appearance';
import { showUrlToolbar } from '../commands/url';

const createHandlerCommand =
	(handler?: () => void): Command =>
	() => {
		handler?.();
		return true;
	};

function wrapManifestHandler(
	handler: (p: NativeEmbedParameterValues, context?: EditorToolbarHandlerContext) => void,
	parameters: NativeEmbedParameterValues,
	handlerContext: EditorToolbarHandlerContext,
): () => Command {
	return () => createHandlerCommand(() => handler(parameters, handlerContext));
}

function wrapManifestHandlerWithContext<C>(
	handler: (
		p: NativeEmbedParameterValues,
		context: C,
		handlerContext?: EditorToolbarHandlerContext,
	) => void,
	parameters: NativeEmbedParameterValues,
	handlerContext: EditorToolbarHandlerContext,
): (context: C) => Command {
	return (context) => createHandlerCommand(() => handler(parameters, context, handlerContext));
}

/**
 * Extracts the hostname from a URL string for use as a fallback display name
 * in the Rovo chat context banner.
 */
function getHostnameFromUrl(url: string): string {
	try {
		return new URL(url).hostname;
	} catch {
		return '';
	}
}

function isMauiNativeEmbed(extensionKey: unknown): boolean {
	return extensionKey === `${NATIVE_EMBED_EXTENSION_KEY}:maui`;
}

function isMauiWhiteboardNativeEmbed(extensionKey: unknown): boolean {
	return extensionKey === `${NATIVE_EMBED_EXTENSION_KEY}:maui-whiteboard`;
}

/**
 * Captures the whiteboard content id from a Confluence whiteboard URL. Numeric ids and
 * UUIDs are both in use, so the segment is taken verbatim. Mirrors the parsers in the
 * MAUI whiteboard experience manifest and `editor-plugin-malleable-ui`'s
 * `replace-whiteboard-embed`.
 */
// eslint-disable-next-line require-unicode-regexp
const WHITEBOARD_CONTENT_ID_REGEX = /\/whiteboard\/([^/?#]+)/;

function getWhiteboardContentId(url: string): string | undefined {
	const contentId = WHITEBOARD_CONTENT_ID_REGEX.exec(url)?.[1];
	if (!contentId) {
		return undefined;
	}
	try {
		return decodeURIComponent(contentId);
	} catch {
		return contentId;
	}
}

function createCopyNodeCommand(
	selectedNativeEmbed: ContentNodeWithPos,
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
): Command {
	return (state, dispatch) => {
		if (!dispatch) {
			return true;
		}

		const node = selectedNativeEmbed.node;
		const nodeWithoutAnnotations = node.mark(
			node.marks.filter((mark) => mark.type.name !== 'annotation'),
		);
		const domNode = toDOM(nodeWithoutAnnotations, state.schema);
		copyDomNode(domNode, nodeWithoutAnnotations.type, state.selection);

		const tr = state.tr;
		const analyticsPayload = getNodeCopiedAnalyticsPayload(node, INPUT_METHOD.FLOATING_TB);
		api?.analytics?.actions.attachAnalyticsEvent(analyticsPayload)(tr);
		tr.setMeta('scrollIntoView', false);
		dispatch(tr);

		return true;
	};
}

/**
 * Opens all unresolved comments on the selected native embed. If there are no
 * unresolved comments, starts a whole-block draft through the annotation plugin.
 */
function createCommentCommand(
	selectedNativeEmbed: ContentNodeWithPos,
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin>,
): Command {
	return (state, dispatch) => {
		const annotationActions = api.annotation?.actions;
		if (!annotationActions) {
			return false;
		}

		// Native embeds use the same block-comment path as media. Passing this
		// through lets the Confluence provider position/open the draft editor from
		// the floating toolbar instead of treating it as a regular selection.
		const isOpeningCommentFromToolbar = fg('cc_maui_annotations_on_extensions') ? true : undefined;
		const openedExistingComments = annotationActions.showCommentForBlockNode(
			selectedNativeEmbed.node,
			VIEW_METHOD.COMMENT_BUTTON,
			isOpeningCommentFromToolbar,
		)(state, dispatch);

		if (openedExistingComments) {
			return true;
		}

		const localId = selectedNativeEmbed.node.attrs?.localId;
		return annotationActions.setInlineCommentDraftState(
			true,
			INPUT_METHOD.FLOATING_TB,
			'block',
			typeof localId === 'string' ? localId : undefined,
			isOpeningCommentFromToolbar,
		)(state, dispatch);
	};
}

/**
 * Creates default EditorToolbarHandlers with actual functionality.
 * Injects editorAPI for commands that need it (e.g. alignment, open-in-new-window).
 */
export function createDefaultEditorToolbarHandlers(
	selectedNativeEmbed: ContentNodeWithPos,
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
	intl: IntlShape,
	schema: Schema,
	parameters: NativeEmbedParameterValues,
): EditorToolbarHandlersWithDefaults {
	const node = selectedNativeEmbed.node;
	const isInViewMode = api?.editorViewMode?.sharedState.currentState()?.mode === 'view';
	// A MAUI whiteboard deliberately stays on the `default` target, so the button keeps
	// reading "Ask Rovo" rather than the "Edit" label a MAUI app block gets. Only the
	// submit behaviour changes for a board, not what the button is called.
	const askRovoTarget =
		!isInViewMode &&
		isMauiNativeEmbed(node.attrs?.extensionKey) &&
		expValEquals('cc-maui-experiment', 'isEnabled', true)
			? 'remix-app'
			: 'default';

	const embedUrl = parameters.url ?? '';
	const embedName = getHostnameFromUrl(embedUrl);

	// A MAUI whiteboard embed renders separate Confluence content, so its prompt can be
	// retargeted at the board rather than handed to the sidebar. Every other native embed
	// keeps `openInRovoChat`, which is what stops an inline edit rewriting the host page's
	// ADF when the embed's content is not addressable from here.
	//
	// In view mode there is no inline edit to retarget, so the sidebar handoff stands.
	const whiteboardContentId =
		!isInViewMode && isMauiWhiteboardNativeEmbed(node.attrs?.extensionKey)
			? getWhiteboardContentId(embedUrl)
			: undefined;
	// The experiment is read last so exposure only fires for an edit-mode MAUI whiteboard
	// whose board id actually resolved, rather than on every native embed toolbar build.
	const inlineEditContentOverride =
		whiteboardContentId && isExperimentEnabled('cc_maui_whiteboard_inline_edit')
			? {
					contentId: whiteboardContentId,
					// Mirrors the existing board update in `useUpdateRemix`: the board is reached
					// by `contentId` alone, with no experience and no block type, so the backend
					// routes to the whiteboard update rather than classifying this as a page edit.
					creationContextParams: {
						source: 'remix' as const,
						isViewMode: false,
					},
				}
			: undefined;

	const askRovoButton = api?.aiExperience?.actions?.getAskRovoFloatingToolbarButton?.({
		intl,
		triggeredFor: schema.nodes.extension,
		target: askRovoTarget,
		inlineEditContentOverride,
		openInRovoChat: !inlineEditContentOverride,
		rovoChatAIFeatureContext:
			embedName && !inlineEditContentOverride
				? {
						projectContext: {
							projectId: embedUrl,
							projectName: embedName,
							projectUrl: embedUrl,
						},
					}
				: undefined,
	});

	const summariseButton = parameters.url
		? api?.aiExperience?.actions?.getSummariseSmartlinkFloatingToolbarButton?.({
				intl,
				smartlinkUrl: parameters.url,
				triggeredFor: schema.nodes.extension,
			})
		: undefined;

	return {
		onAppearanceClick: (appearance: NativeEmbedAppearance) =>
			setNativeEmbedAppearance(selectedNativeEmbed, appearance, api?.analytics?.actions),
		onAlignmentClick: (alignment: AlignmentValue) =>
			createUpdateAlignmentCommand(api, alignment, node),
		onCommentClick: api?.annotation
			? () => createCommentCommand(selectedNativeEmbed, api)
			: undefined,
		onCopyClick: () => createCopyNodeCommand(selectedNativeEmbed, api),
		onCopyLinkClick: () => createCopyLinkCommand(node),
		onDeleteClick: () => createDeleteCommand(selectedNativeEmbed),
		onShowTitleClick: () => createHandlerCommand(createToggleAlwaysShowTitle(api, node)),
		onExpandClick: () => (_state, _dispatch, view) => {
			if (view) {
				try {
					const domRef = findDomRefAtPos(selectedNativeEmbed.pos, view.domAtPos.bind(view));
					if (domRef instanceof HTMLElement) {
						const extensionContainer = domRef.classList.contains('extension-container')
							? domRef
							: ((domRef.querySelector('.extension-container') as HTMLElement | null) ?? domRef);
						extensionContainer.dispatchEvent(
							new CustomEvent('native-embed-open-fullscreen', { bubbles: true }),
						);
					}
				} catch {
					// ignore
				}
			}
			return true;
		},
		onOpenInNewWindowClick: () => createOpenInNewWindowCommand(node),
		onEditUrlClick: () => showUrlToolbar,
		onEditClick: () => showUrlToolbar,
		askRovoButton,
		onAskRovoClick: askRovoButton
			? () => (state, dispatch, view) => {
					// Stage the board as the inline-Rovo streaming target so the loading
					// overlay and the post-stream ready bar resolve to this node, the same
					// way the remix type switcher stages it for this extension key. Only the
					// retargeted path streams into the editor; the sidebar handoff does not.
					if (inlineEditContentOverride) {
						const localId = node.attrs?.localId;
						if (typeof localId === 'string' && localId.length > 0) {
							api?.aiExperience?.actions?.stageInlineRovoStreaming?.(localId);
						}
					}
					return askRovoButton.onClick ? askRovoButton.onClick(state, dispatch, view) : false;
				}
			: undefined,
		getRemixButton: fg('maui_remixable_page_native_embeds')
			? api?.malleableUi?.actions?.getRemixFloatingToolbarButton
			: undefined,
		getRemixVariantButton: api?.malleableUi?.actions?.getRemixVariantFloatingToolbarButton,
		onSummariseClick: () => summariseButton?.onClick ?? createHandlerCommand(() => {}),
		onRefreshClick: () => createHandlerCommand(() => {}),
		onChangeBorderClick: () => createHandlerCommand(() => {}),
		onSetEmbedTypeClick: () => createHandlerCommand(() => {}),
	};
}

/**
 * Builds an EditorToolbarHandlersWithDefaults object by merging manifest handlers with default handlers.
 * For each handler key, uses the manifest handler if defined, otherwise the default action.
 */
export function buildToolbarHandlers(
	selectedNativeEmbed: ContentNodeWithPos,
	manifestHandlers: EditorToolbarHandlers | undefined,
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
	parameters: NativeEmbedParameterValues,
	intl: IntlShape,
	schema: Schema,
): EditorToolbarHandlersWithDefaults {
	const defaultHandlers = createDefaultEditorToolbarHandlers(
		selectedNativeEmbed,
		api,
		intl,
		schema,
		parameters,
	);
	const result: EditorToolbarHandlersWithDefaults = { ...defaultHandlers };
	const selectedNodeLocalId =
		typeof selectedNativeEmbed.node.attrs?.localId === 'string'
			? selectedNativeEmbed.node.attrs.localId
			: undefined;
	const handlerContext: EditorToolbarHandlerContext = {
		editorApi: api,
		selectedNodeLocalId,
	};

	for (const key of EDITOR_TOOLBAR_HANDLER_KEYS) {
		const manifestHandler = manifestHandlers?.[key];
		if (!manifestHandler) continue;

		switch (key) {
			case 'onAlignmentClick':
				result.onAlignmentClick = wrapManifestHandlerWithContext<AlignmentValue>(
					manifestHandler as NonNullable<EditorToolbarHandlers['onAlignmentClick']>,
					parameters,
					handlerContext,
				);
				break;
			case 'onAppearanceClick':
				result.onAppearanceClick = wrapManifestHandlerWithContext<NativeEmbedAppearance>(
					manifestHandler as NonNullable<EditorToolbarHandlers['onAppearanceClick']>,
					parameters,
					handlerContext,
				);
				break;
			case 'onAskRovoClick':
				if (fg('platform_editor_maui_jira_updates')) {
					result[key] =
						result[key] !== undefined
							? wrapManifestHandler(
									manifestHandler as (
										p: NativeEmbedParameterValues,
										context?: EditorToolbarHandlerContext,
									) => void,
									parameters,
									handlerContext,
								)
							: undefined;
				} else {
					result[key] = wrapManifestHandler(
						manifestHandler as (
							p: NativeEmbedParameterValues,
							context?: EditorToolbarHandlerContext,
						) => void,
						parameters,
						handlerContext,
					);
				}
				break;
			default:
				result[key] = wrapManifestHandler(
					manifestHandler as (
						p: NativeEmbedParameterValues,
						context?: EditorToolbarHandlerContext,
					) => void,
					parameters,
					handlerContext,
				);
		}
	}

	return result;
}
